import {
  type AnimationEvent,
  type ComponentPropsWithoutRef,
  type FC,
  type PropsWithChildren,
  type Ref,
  createContext,
  useContext,
  useEffect,
  useRef,
} from 'react';
import {
  type ModalType,
  useBackModal,
  useCloseModal,
  useIsCurrentModal,
  useIsModalClosing,
  useModalEntryKey,
  useRemoveClosedModal,
} from '@/hooks/data/modal/useModalStore';
import { AlertDialog, AlertDialogContent } from '@/components/ui/alert-dialog';
import { Dialog, DialogContent } from '@/components/ui/dialog';

type ModalProps = {
  id: ModalType;
  backOnClose?: boolean;
  variant?: 'dialog' | 'alert';
  onOpenChange?: (open: boolean) => void;
};

type ModalContentProps = ComponentPropsWithoutRef<typeof DialogContent>;

type ModalPrimitiveContextValue = {
  Content: typeof DialogContent | typeof AlertDialogContent;
  entryKey?: string;
  isClosing: boolean;
  removeClosedModal: (key: string) => void;
};

const ModalPrimitiveContext = createContext<ModalPrimitiveContextValue | null>(null);

const useModalPrimitives = () => {
  const primitives = useContext(ModalPrimitiveContext);

  if (!primitives) {
    throw new Error('Modal primitives missing. Wrap modal content with <Modal>.');
  }

  return primitives;
};

// Safety net for the drain, NOT the primary path.
//
// onAnimationEnd below stays in charge of the normal close: it fires while the content is
// still mounted, so the entry is removed in the same beat and the whole dialog — including
// anything it portals, such as a date picker — disappears together. Draining only on unmount
// instead leaves a window where the content is gone but the dialog is still mounted, which
// surfaces as a portalled popover flashing after the modal closes.
//
// But an animationend that never fires used to strand the entry forever: invisible, still
// mounted, and still counted by useModalStackLength (which several dialogs branch on). That
// happens under reduced motion, in a display:none container, or if the animate-out utilities
// are ever dropped. Radix's Presence unmounts content in exactly those cases too, so this
// cleanup catches whatever the animation path missed. removeClosed is keyed and idempotent,
// so the usual double-call after a normal close is a no-op.
//
// The isClosing gate is essential: content also unmounts when this modal is merely COVERED by
// a child (open={false} while isClosing is false), and draining then would evict a parent that
// is meant to stay in the stack retaining its form state.
const DrainClosedEntryOnUnmount = () => {
  const { entryKey, isClosing, removeClosedModal } = useModalPrimitives();
  const latest = useRef({ entryKey, isClosing, removeClosedModal });

  latest.current = { entryKey, isClosing, removeClosedModal };

  useEffect(
    () => () => {
      const { entryKey: key, isClosing: closing, removeClosedModal: remove } = latest.current;

      if (!closing || !key) {
        return;
      }

      // Deferred past the current commit: this runs while React unmounts the content, and
      // removing the entry synchronously would re-render ModalHost mid-commit.
      queueMicrotask(() => remove(key));
    },
    [],
  );

  return null;
};

export const ModalContent = ({
  children,
  onAnimationEnd,
  ...props
}: ModalContentProps & { ref?: Ref<HTMLDivElement> }) => {
  const { Content, entryKey, isClosing, removeClosedModal } = useModalPrimitives();

  return (
    <Content
      {...props}
      onAnimationEnd={(event: AnimationEvent<HTMLDivElement>) => {
        try {
          onAnimationEnd?.(event);
        } finally {
          if (isClosing && entryKey && event.currentTarget === event.target) {
            removeClosedModal(entryKey);
          }
        }
      }}
    >
      {children}
      <DrainClosedEntryOnUnmount />
    </Content>
  );
};

export const Modal: FC<PropsWithChildren<ModalProps>> = ({
  children,
  id,
  backOnClose = false,
  variant = 'dialog',
  onOpenChange,
}) => {
  const closeModal = useCloseModal();
  const backModal = useBackModal();
  const removeClosedModal = useRemoveClosedModal();
  const entryKey = useModalEntryKey(id);
  const isClosing = useIsModalClosing(id);

  const isOpen = useIsCurrentModal(id);

  const handleCloseModal = () => {
    if (backOnClose) {
      backModal();
    } else {
      closeModal(id);
    }
  };

  const Component = variant === 'dialog' ? Dialog : AlertDialog;
  const Content = variant === 'dialog' ? DialogContent : AlertDialogContent;

  return (
    <Component
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleCloseModal();
        }

        onOpenChange?.(open);
      }}
    >
      <ModalPrimitiveContext.Provider value={{ Content, entryKey, isClosing, removeClosedModal }}>
        {children}
      </ModalPrimitiveContext.Provider>
    </Component>
  );
};
